import {
    createGroup,
    deleteGroup,
    generateLicenses,
    revokeLicense,
    updateGroupMembers,
    updateGroupParent,
} from "@dashboard/devices/store";
import { filesApi } from "@dashboard/files/api";
import { policiesApi } from "@dashboard/policies/api";
import { productsApi } from "@dashboard/products/api";
import { createProvisioning, deleteProvisioning } from "@dashboard/provisioning/store";
import { fdroidsApi } from "@dashboard/repositories/api";
import { isAnyOf, ListenerMiddlewareInstance, UnsubscribeListener } from "@reduxjs/toolkit";

import { workspacesApi } from ".";

// Groups, provisionings and licenses change the workspace object itself (its members and the subscription's
// group ids), so both tags have to go. Everything below only moves a usage counter, which lives on
// WorkspaceFeatures — invalidating "Workspaces" for those refetched /workspaces twice over (it is provided by
// both the current-workspace and the all-workspaces cache entries) on every policy, product, build, repository
// or file write, and each refetch re-runs the workspace resolution in usePrefetch.
const workspaceActions = isAnyOf(
    updateGroupMembers.fulfilled,
    deleteGroup.fulfilled,
    createGroup.fulfilled,
    updateGroupParent.fulfilled,
    createProvisioning.fulfilled,
    deleteProvisioning.fulfilled,
    generateLicenses.fulfilled,
    revokeLicense.fulfilled,
);

const featureUsageActions = isAnyOf(
    policiesApi.endpoints.createPolicy.matchFulfilled,
    policiesApi.endpoints.deletePolicy.matchFulfilled,
    productsApi.endpoints.createProduct.matchFulfilled,
    productsApi.endpoints.deleteProduct.matchFulfilled,
    productsApi.endpoints.createBuild.matchFulfilled,
    productsApi.endpoints.deleteBuild.matchFulfilled,
    productsApi.endpoints.importProductBuild.matchFulfilled,
    fdroidsApi.endpoints.createFdroid.matchFulfilled,
    fdroidsApi.endpoints.deleteFdroid.matchFulfilled,
    filesApi.endpoints.uploadFile.matchFulfilled,
    filesApi.endpoints.deleteFile.matchFulfilled,
);

const workspacesActions = isAnyOf(workspaceActions, featureUsageActions);

export const initWorkspacesCacheListener = (listenerMiddleware: ListenerMiddlewareInstance): UnsubscribeListener => {
    return listenerMiddleware.startListening({
        matcher: workspacesActions,
        effect: (action, listenerApi) => {
            const tags = workspaceActions(action)
                ? (["Workspaces", "WorkspaceFeatures"] as const)
                : (["WorkspaceFeatures"] as const);

            listenerApi.dispatch(workspacesApi.util.invalidateTags([...tags]));
        },
    });
};
